// Content keys. Arrays fix the order; a key's index is its stable numeric type id, used on the wire and
// in components, so append new keys at the end only.

export const UNIT_KEYS = [
  'conscripts',
  'tank',
  'artillery',
  'mobile_sam',
  'fighter',
  'bomber',
  'drone_swarm',
  'destroyer',
  'carrier',
] as const
export type UnitKey = (typeof UNIT_KEYS)[number]

export const BUILDING_KEYS = ['tax_office', 'airbase', 'port', 'sam_site', 'fortress', 'propaganda_office'] as const
export type BuildingKey = (typeof BUILDING_KEYS)[number]

export const STRIKE_KEYS = ['precision_strike', 'shock_and_awe', 'sanctions', 'leaflet_drop'] as const
export type StrikeKey = (typeof STRIKE_KEYS)[number]

export const TAX_SETTINGS = ['relief', 'low', 'standard', 'high', 'wartime'] as const
export type TaxSetting = (typeof TAX_SETTINGS)[number]

export const AI_TIERS = ['recruit', 'veteran', 'general', 'warlord'] as const
export type AiTier = (typeof AI_TIERS)[number]

/** Game modes (docs/GAME-MODES-ROADMAP.md): each is one GAME_MODES entry. */
export const GAME_MODE_KEYS = ['duel', 'world_war'] as const
export type GameModeKey = (typeof GAME_MODE_KEYS)[number]

/** How a unit type picks its deploy city among the cities that can launch it (GDD §7.1). */
export const DEPLOY_DOCTRINES = ['frontline', 'richest', 'rearmost', 'capital', 'loudest', 'alphabetical', 'lowest_bidder'] as const
export type DeployDoctrine = (typeof DEPLOY_DOCTRINES)[number]

/** How an aimed strike picks its target among the enemies in sight (GDD §7.1). */
export const STRIKE_TARGETINGS = ['priciest', 'crowd', 'closest_to_home'] as const
export type StrikeTargeting = (typeof STRIKE_TARGETINGS)[number]

/** Which own city a missile launches from, relative to its target (GDD §7.1). */
export const LAUNCH_DOCTRINES = ['nearest', 'farthest'] as const
export type LaunchDoctrine = (typeof LAUNCH_DOCTRINES)[number]

/** How a missile steers (GDD §7.1): after its target while the target exists, or to the point it was aimed at. */
export const MISSILE_GUIDANCES = ['homing', 'point'] as const
export type MissileGuidance = (typeof MISSILE_GUIDANCES)[number]

/** Movement domain of a unit or of a city-graph edge. */
export const DOMAINS = ['land', 'sea', 'air'] as const
export type Domain = (typeof DOMAINS)[number]

/** GDD §8 target classes. */
export const TARGET_CLASSES = ['any', 'ground', 'structures_only', 'air_only', 'air_and_ground_units', 'none'] as const
export type TargetClass = (typeof TARGET_CLASSES)[number]

declare const typeIdBrand: unique symbol
/** Stable numeric id of a content key: its index in the matching *_KEYS array. */
export type TypeId<K extends string> = number & { readonly [typeIdBrand]: K }

export function typeIdOf<K extends string>(keys: readonly K[], key: K): TypeId<K> {
  const index = keys.indexOf(key)
  if (index < 0) throw new RangeError(`unknown content key: ${key}`)
  return index as TypeId<K>
}

export function keyOf<K extends string>(keys: readonly K[], id: number): K {
  const key = keys[id]
  if (key === undefined) throw new RangeError(`unknown content type id: ${id}`)
  return key
}
